import { supabase } from "@/integrations/supabase/client";

// Chunk size: 5MB (must match edge function limit)
const CHUNK_SIZE = 5 * 1024 * 1024;

interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

interface GCSUploadOptions {
  file: File;
  fileType: 'wav' | 'flp';
  onProgress?: (progress: UploadProgress) => void;
}

interface GCSUploadResult {
  success: boolean;
  url?: string;
  error?: string;
}

export async function uploadToGCS({ file, fileType, onProgress }: GCSUploadOptions): Promise<GCSUploadResult> {
  try {
    if (import.meta.env.DEV) {
      console.log(`[GCS] Starting chunked upload for ${fileType}:`, file.name, `Size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
    }

    // 1) Initiate resumable upload session
    const { data: initData, error: initError } = await supabase.functions.invoke('gcs-initiate-resumable', {
      body: {
        fileName: file.name,
        fileType,
        contentType: file.type || 'application/octet-stream',
        size: file.size,
      },
    });

    if (initError || !initData?.uploadUrl) {
      if (import.meta.env.DEV) console.error('[GCS] Init error:', initError);
      return { success: false, error: initError?.message || 'Failed to initiate upload' };
    }

    const { uploadUrl, bucket, fileName } = initData;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    if (import.meta.env.DEV) {
      console.log(`[GCS] Upload session created. Chunks: ${totalChunks}, URL received`);
    }

    // 2) Upload file in chunks
    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      if (import.meta.env.DEV) {
        console.log(`[GCS] Uploading chunk ${chunkIndex + 1}/${totalChunks}: bytes ${start}-${end - 1}`);
      }

      const formData = new FormData();
      formData.append('uploadUrl', uploadUrl);
      formData.append('chunk', chunk);
      formData.append('chunkIndex', chunkIndex.toString());
      formData.append('totalChunks', totalChunks.toString());
      formData.append('totalSize', file.size.toString());
      formData.append('contentType', file.type || 'application/octet-stream');

      const { data: chunkData, error: chunkError } = await supabase.functions.invoke('gcs-upload-chunk', {
        body: formData,
      });

      if (chunkError || !chunkData?.success) {
        if (import.meta.env.DEV) console.error(`[GCS] Chunk ${chunkIndex + 1} failed:`, chunkError);
        return { success: false, error: chunkError?.message || `Failed to upload chunk ${chunkIndex + 1}` };
      }

      // Report progress
      if (onProgress) {
        onProgress({
          loaded: end,
          total: file.size,
          percentage: Math.round((end / file.size) * 100),
        });
      }

      if (import.meta.env.DEV) {
        console.log(`[GCS] Chunk ${chunkIndex + 1}/${totalChunks} complete`);
      }
    }

    if (import.meta.env.DEV) {
      console.log(`[GCS] Upload complete: gcs://${bucket}/${fileName}`);
    }

    return { 
      success: true, 
      url: `gcs://${bucket}/${fileName}` 
    };

  } catch (err) {
    if (import.meta.env.DEV) console.error('[GCS] Upload failed:', err);
    return { 
      success: false, 
      error: err instanceof Error ? err.message : 'Upload failed' 
    };
  }
}
